import { useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";

const TYPE_CONFIG = {
  airtime: { icon: "ri-phone-line", color: "#059669", label: "Airtime Purchase" },
  data: { icon: "ri-wifi-line", color: "#0284C7", label: "Data Bundle" },
  electricity: { icon: "ri-flashlight-line", color: "#D97706", label: "Electricity Bill" },
  tv: { icon: "ri-tv-2-line", color: "#7C3AED", label: "TV Subscription" },
  internet: { icon: "ri-router-line", color: "#0F766E", label: "Internet Bill" },
  betting: { icon: "ri-gamepad-line", color: "#DC2626", label: "Betting Wallet" },
};

export default function BillSuccessPage() {
  const navigate = useNavigate();
  const { state } = useLocation();
  const hasNavigated = useRef(false);

  useEffect(() => {
    if (!state && !hasNavigated.current) {
      hasNavigated.current = true;
      navigate("/bills");
    }
  }, [state, navigate]);

  if (!state) return null;

  const { type, title, detail, subDetail, amount, requestId, transactionId, token } = state;
  const cfg = TYPE_CONFIG[type] || TYPE_CONFIG.airtime;

  const now = new Date();
  const timeStr = now.toLocaleString("en-NG", {
    day: "numeric", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });

  const displayRef = transactionId || requestId || "N/A";

  return (
    <div className="min-h-screen bg-[#F7F8FA] flex flex-col">
      {/* Celebration header */}
      <div
        className="px-5 pt-16 pb-10 text-center relative overflow-hidden"
        style={{ background: "linear-gradient(135deg, #0F4C35 0%, #1A6B4A 100%)" }}
      >
        <div className="absolute top-0 right-0 w-48 h-48 rounded-full opacity-10"
          style={{ background: "radial-gradient(circle, #fff 0%, transparent 70%)", transform: "translate(30%, -30%)" }} />
        <div className="absolute bottom-0 left-0 w-32 h-32 rounded-full opacity-10"
          style={{ background: "radial-gradient(circle, #fff 0%, transparent 70%)", transform: "translate(-30%, 30%)" }} />

        <div className="relative z-10">
          <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4 border-4 border-white/30">
            <i className="ri-checkbox-circle-fill text-white text-4xl" />
          </div>
          <h1 className="text-white text-2xl font-bold mb-1" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
            Payment Successful!
          </h1>
          <p className="text-emerald-200 text-sm">Your bill has been paid</p>
          <div className="mt-4">
            <p className="text-emerald-200 text-sm">Amount Paid</p>
            <p className="text-4xl font-bold mt-1 text-white" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              ₦{amount.toLocaleString()}
            </p>
          </div>
        </div>
      </div>

      <div className="flex-1 px-5 -mt-5">
        {/* Electricity token — shown prominently for prepaid meters */}
        {token && (
          <div className="bg-amber-50 border-2 border-amber-300 rounded-2xl p-4 mb-4 text-center">
            <p className="text-amber-700 text-xs font-semibold uppercase tracking-wide mb-2">
              ⚡ Your Meter Token
            </p>
            <p className="text-amber-900 font-mono font-bold text-2xl tracking-widest">{token}</p>
            <p className="text-amber-600 text-xs mt-2">Enter this token on your prepaid meter</p>
          </div>
        )}

        {/* Receipt card */}
        <div className="bg-white rounded-3xl border border-slate-100 shadow-lg overflow-hidden">
          <div className="relative h-6 bg-[#F7F8FA]">
            <div className="absolute -bottom-3 left-0 right-0 h-6 bg-white rounded-t-3xl" />
            <div className="absolute bottom-0 -left-3 w-6 h-6 bg-[#F7F8FA] rounded-full" />
            <div className="absolute bottom-0 -right-3 w-6 h-6 bg-[#F7F8FA] rounded-full" />
          </div>

          <div className="px-5 pb-6 pt-2">
            <div className="flex justify-center mb-5">
              <div className="w-12 h-12 rounded-2xl flex items-center justify-center" style={{ background: cfg.color + "15" }}>
                <i className={`${cfg.icon} text-xl`} style={{ color: cfg.color }} />
              </div>
            </div>

            <div className="space-y-0">
              {[
                { label: "Service", value: title },
                { label: "Recipient", value: detail },
                subDetail && { label: "Account", value: subDetail },
                { label: "Date & Time", value: timeStr },
                { label: "Reference", value: displayRef },
                { label: "Status", value: "✅ Successful" },
              ]
                .filter(Boolean)
                .map((row) => (
                  <div key={row.label} className="flex justify-between items-center py-2.5 border-b border-slate-50">
                    <span className="text-slate-400 text-sm">{row.label}</span>
                    <span className="text-slate-800 font-semibold text-sm text-right max-w-[55%] break-all">
                      {row.value}
                    </span>
                  </div>
                ))}
            </div>

            <button className="w-full mt-5 border-2 border-slate-200 text-slate-600 py-3 rounded-2xl font-semibold text-sm flex items-center justify-center gap-2 hover:bg-slate-50 transition-colors">
              <i className="ri-share-line" />
              Share Receipt
            </button>
          </div>
        </div>

        {/* Actions */}
        <div className="mt-4 space-y-3 pb-10">
          <button
            onClick={() => navigate("/bills")}
            className="w-full bg-emerald-600 hover:bg-emerald-700 text-white py-4 rounded-2xl font-bold text-sm transition-all active:scale-95"
          >
            Pay Another Bill
          </button>
          <button
            onClick={() => navigate("/home")}
            className="w-full text-slate-500 py-3 text-sm font-medium"
          >
            Back to Home
          </button>
        </div>
      </div>
    </div>
  );
}
